import { useState, useEffect, useMemo } from 'react';
import {
  useTaxonomyEntries,
  type TaxonomyEntryDto,
} from '../api/queries/taxonomy';
import type { SelectOption } from '../components/common/FilterableSelect';

/**
 * Adapter hook that bridges taxonomy API entries into SelectOption[] format
 * for use with FilterableSelect. Includes debounced search.
 */
export function useTaxonomyOptions(
  categoryKey: string | undefined,
  inputValue: string,
) {
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(inputValue.length >= 1 ? inputValue : '');
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const { data, isLoading } = useTaxonomyEntries({
    categoryKey,
    search: debouncedSearch || undefined,
    pageSize: 500,
  });

  const options: SelectOption[] = useMemo(() => {
    if (!data?.items) return [];
    return data.items.map((entry: TaxonomyEntryDto) => ({
      value: entry.name,
      label: entry.name,
    }));
  }, [data?.items]);

  return { options, isLoading };
}
